import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Link, Clock, Users, Globe, MessageSquare, FileText, AlertCircle, Check, Trash2, Eye, EyeOff, Activity, TrendingUp, Edit, ChevronUp, ChevronDown, FileEdit, Paperclip } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import { copyToClipboard } from '../utils/clipboard';
import SessionAccessManager from './SessionAccessManager';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionTitle?: string;
  embedded?: boolean;
}

interface ShareLink {
  id: string;
  label: string;
  url: string;
  shareToken: string;
  createdAt: string;
  expiresAt: string | null;
  maxUniqueIps: number | null;
  maxTotalViews: number | null;
  isEnabled: boolean;
  status: 'active' | 'expired' | 'disabled';
  uniqueViews: number;
  uniqueIps: number;
  totalViews: number;
  llmCallsUsed: number;
  llmCallCap: number;
  chatMode: 'DISABLED' | 'READ_ONLY' | 'FULL';
  timelineEnabled: boolean;
  rankingEnabled: boolean;
  rankingTtmlAllowed: boolean;
  commentsRead: boolean;
  commentsWrite: boolean;
  descriptionEnabled: boolean;
  contextFilesEnabled?: boolean;
  questionsEnabled?: boolean;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  sessionTitle = 'Untitled Session',
  embedded = false
}) => {
  const { isDarkMode } = useDarkMode();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'create' | 'existing' | 'access'>('existing');
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedLinkForLogs, setSelectedLinkForLogs] = useState<ShareLink | null>(null);
  const [accessLogs, setAccessLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsGroupBy, setLogsGroupBy] = useState<'none' | 'ip' | 'action' | 'user-agent'>('none');
  const [activityData, setActivityData] = useState<Record<string, any>>({});
  const [loadingActivity, setLoadingActivity] = useState<Record<string, boolean>>({});
  const [activityTimeRange, setActivityTimeRange] = useState<Record<string, string>>({});
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [expandedActivityGraphs, setExpandedActivityGraphs] = useState<Record<string, boolean>>({});
  
  // Form state for new share link
  // Note: Timeline is now always available via the interactive timeline at the bottom
  const [formData, setFormData] = useState({
    label: '',
    expiresIn: 'never', // Default to never expire
    maxUniqueIps: '',
    maxTotalViews: '', // New field for total views limit
    chatEnabled: false,
    rankingEnabled: true,
    rankingTtmlAllowed: false,
    commentsRead: false,
    commentsWrite: false,
    descriptionEnabled: false,
    contextFilesEnabled: true,
    questionsEnabled: false,
  });

  const tabActiveClass = isDarkMode
    ? 'text-brand-yellow border-b-2 border-brand-yellow'
    : 'text-brand-navy border-b-2 border-brand-yellow';
  const inputClass = `w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow/30 focus:border-brand-yellow ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-white'
      : 'bg-white border-gray-300 text-gray-900'
  }`;
  const checkboxClass = 'mt-1 rounded border-gray-300 text-brand-navy focus:ring-brand-yellow';
  const primaryButtonClass = 'px-4 py-2 rounded-lg bg-brand-yellow text-brand-navy hover:bg-[#ffc54d] transition-colors disabled:opacity-50';

  const fetchShareLinks = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // Add timestamp to prevent caching
      const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/share-links?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch share links');
      }
      
      const links = await response.json();
      console.log('Fetched share links:', links);
      console.log('First link details:', links[0]);
      if (links.length > 0) {
        console.log('First link details:', {
          id: links[0].id,
          isEnabled: links[0].isEnabled,
          is_enabled: links[0].is_enabled,
          status: links[0].status
        });
      }
      setShareLinks(links);
      
      setActiveTab((currentTab) => {
        if (currentTab === 'access') {
          return currentTab;
        }
        return links.length > 0 ? 'existing' : 'create';
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch share links');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, token]);

  useEffect(() => {
    if (isOpen && sessionId) {
      void fetchShareLinks();
    }
  }, [fetchShareLinks, isOpen, sessionId]);

  const createShareLink = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/share-links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          label: formData.label || `Share link for ${sessionTitle}`,
          expiresIn: formData.expiresIn === 'never' ? null : parseInt(formData.expiresIn),
          maxUniqueIps: formData.maxUniqueIps ? parseInt(formData.maxUniqueIps) : null,
          maxTotalViews: formData.maxTotalViews ? parseInt(formData.maxTotalViews) : null,
          chatMode: formData.chatEnabled ? 'FULL' : 'DISABLED',
          llmCallCap: 0,
          timelineEnabled: false, // Timeline is always available via interactive timeline
          rankingEnabled: formData.rankingEnabled,
          rankingTtmlAllowed: false,
          commentsRead: formData.commentsRead,
          commentsWrite: formData.commentsWrite,
          descriptionEnabled: formData.descriptionEnabled,
          contextFilesEnabled: formData.contextFilesEnabled,
          questionsEnabled: formData.questionsEnabled,
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create share link');
      }
      
      await fetchShareLinks();
      setActiveTab('existing');
      
      // Reset form
      setFormData({
        label: '',
        expiresIn: 'never',
        maxUniqueIps: '',
        maxTotalViews: '',
        chatEnabled: false,
        rankingEnabled: true,
        rankingTtmlAllowed: false,
        commentsRead: false,
        commentsWrite: false,
        descriptionEnabled: false,
        contextFilesEnabled: true,
        questionsEnabled: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShareLink = async (linkId: string, isEnabled: boolean) => {
    console.log('Toggling share link:', linkId, 'from', isEnabled, 'to', !isEnabled);
    try {
      const response = await fetch(`${API_BASE_URL}/api/share-links/${linkId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_enabled: !isEnabled })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update share link');
      }
      
      const result = await response.json();
      console.log('Toggle result:', result);
      
      await fetchShareLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update share link');
    }
  };

  const editShareLink = (link: ShareLink) => {
    // Populate form with existing link data
    // Note: Timeline is always available via interactive timeline

    // Determine expiresIn value based on current expiresAt
    let expiresInValue = 'never';
    if (link.expiresAt) {
      // If it has an expiry, default to 1 week for the dropdown
      // (since we can't reverse-calculate the original duration)
      expiresInValue = '168';
    }

    setFormData({
      label: link.label,
      expiresIn: expiresInValue,
      maxUniqueIps: link.maxUniqueIps?.toString() || '',
      maxTotalViews: link.maxTotalViews?.toString() || '',
      chatEnabled: link.chatMode !== 'DISABLED',
      rankingEnabled: link.rankingEnabled || false,
      rankingTtmlAllowed: false,
      commentsRead: link.commentsRead || false,
      commentsWrite: link.commentsWrite || false,
      descriptionEnabled: link.descriptionEnabled || false,
      contextFilesEnabled: link.contextFilesEnabled !== false,
      questionsEnabled: link.questionsEnabled === true,
    });
    setEditingLinkId(link.id);
    setActiveTab('create');
  };

  const updateShareLink = async () => {
    if (!editingLinkId) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const updates: any = {
        label: formData.label
      };

      // Handle expiry - convert expiresIn (hours) to expires_at (datetime or null)
      if (formData.expiresIn === 'never') {
        updates.expires_at = null;
      } else if (formData.expiresIn) {
        const expiresAt = new Date(Date.now() + parseInt(formData.expiresIn) * 60 * 60 * 1000);
        updates.expires_at = expiresAt.toISOString();
      }

      // Only include fields that should be updated
      if (formData.maxUniqueIps) {
        updates.max_unique_ips = parseInt(formData.maxUniqueIps);
      } else {
        updates.max_unique_ips = null;
      }

      if (formData.maxTotalViews) {
        updates.max_total_views = parseInt(formData.maxTotalViews);
      } else {
        updates.max_total_views = null;
      }

      updates.chat_mode = formData.chatEnabled ? 'FULL' : 'DISABLED';
      updates.llm_call_cap = 0;
      updates.timeline_enabled = false; // Timeline is always available via interactive timeline
      updates.ranking_enabled = formData.rankingEnabled;
      updates.ranking_ttml_allowed = false;
      updates.comments_read = formData.commentsRead;
      updates.comments_write = formData.commentsWrite;
      updates.description_enabled = formData.descriptionEnabled;
      updates.context_files_enabled = formData.contextFilesEnabled;
      updates.questions_enabled = formData.questionsEnabled;

      const response = await fetch(`${API_BASE_URL}/api/share-links/${editingLinkId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update share link');
      }
      
      // Reset form and fetch updated links
      setEditingLinkId(null);
      setFormData({
        label: '',
        expiresIn: 'never',
        maxUniqueIps: '',
        maxTotalViews: '',
        chatEnabled: false,
        rankingEnabled: true,
        rankingTtmlAllowed: false,
        commentsRead: false,
        commentsWrite: false,
        descriptionEnabled: false,
        contextFilesEnabled: true,
        questionsEnabled: false,
      });
      setActiveTab('existing');
      await fetchShareLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update share link');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteShareLink = async (linkId: string) => {
    if (!window.confirm('Are you sure you want to delete this share link?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/share-links/${linkId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete share link');
      }
      
      await fetchShareLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete share link');
    }
  };

  const handleCopyToClipboard = async (url: string, linkId: string) => {
    const success = await copyToClipboard(url);
    if (success) {
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } else {
      setError('Failed to copy to clipboard');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return 'Never';
    const date = new Date(expiresAt);
    const now = new Date();
    if (date < now) return 'Expired';
    
    const diff = date.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  const fetchLogs = async (link: ShareLink) => {
    setLogsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/share-links/${link.id}/logs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data = await response.json();
      setAccessLogs(data.logs || []);
      setSelectedLinkForLogs(link);
      setShowLogsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchActivityData = async (linkId: string, timeRange?: string) => {
    const range = timeRange || activityTimeRange[linkId] || '7days';
    
    setLoadingActivity(prev => ({ ...prev, [linkId]: true }));
    setActivityTimeRange(prev => ({ ...prev, [linkId]: range }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/share-links/${linkId}/logs?limit=10000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch activity data');
      }
      
      const data = await response.json();
      const logs = data.logs || [];
      
      // Determine time range
      const now = new Date();
      let startTime: Date;
      let bucketSize: 'hour' | 'day' | 'week';
      
      switch (range) {
        case '24hours':
          startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          bucketSize = 'hour';
          break;
        case '7days':
          startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          bucketSize = 'hour';
          break;
        case '30days':
          startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          bucketSize = 'day';
          break;
        case 'all':
          startTime = new Date(0);
          bucketSize = 'day';
          break;
        default:
          startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          bucketSize = 'hour';
      }
      
      const bucketData: Record<string, { views: number, uniqueIps: Set<string> }> = {};
      
      // Debug: log the data we're processing
      console.log(`Processing ${logs.length} logs for range ${range}, startTime: ${startTime.toISOString()}`);
      
      logs.forEach((log: any) => {
        // Handle SQLite timestamp format: "YYYY-MM-DD HH:MM:SS"
        const logDate = new Date(log.timestamp.replace(' ', 'T'));
        
        // Count all TAB_VIEW events (main page views)
        if (logDate >= startTime && log.event === 'TAB_VIEW') {
          let bucketKey: string;
          
          if (bucketSize === 'hour') {
            bucketKey = new Date(logDate.getFullYear(), logDate.getMonth(), logDate.getDate(), logDate.getHours()).toISOString();
          } else if (bucketSize === 'day') {
            bucketKey = new Date(logDate.getFullYear(), logDate.getMonth(), logDate.getDate()).toISOString();
          } else {
            // week
            const weekStart = new Date(logDate);
            weekStart.setDate(logDate.getDate() - logDate.getDay());
            weekStart.setHours(0, 0, 0, 0);
            bucketKey = weekStart.toISOString();
          }
          
          if (!bucketData[bucketKey]) {
            bucketData[bucketKey] = { views: 0, uniqueIps: new Set() };
          }
          bucketData[bucketKey].views++;
          bucketData[bucketKey].uniqueIps.add(log.ip_address);
        }
      });
      
      // Convert to array format for display
      const chartData = Object.entries(bucketData)
        .map(([time, data]) => ({
          time: new Date(time),
          views: data.views,
          uniqueIps: data.uniqueIps.size,
          bucketSize
        }))
        .sort((a, b) => a.time.getTime() - b.time.getTime());
      
      // Debug: log the chart data
      console.log('Chart data:', chartData);
      console.log('Sample bucket:', chartData[0]);
      
      setActivityData(prev => ({ ...prev, [linkId]: chartData }));
    } catch (err) {
      console.error('Failed to fetch activity data:', err);
    } finally {
      setLoadingActivity(prev => ({ ...prev, [linkId]: false }));
    }
  };

  if (!isOpen) return null;

  const shellClassName = embedded
    ? `w-full overflow-hidden rounded-2xl border ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`
    : `relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    }`;

  const contentClassName = embedded ? 'overflow-y-auto' : 'overflow-y-auto max-h-[calc(90vh-200px)]';

  return (
    <div className={embedded ? '' : 'fixed inset-0 z-50 flex items-center justify-center p-4'}>
      {!embedded && <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />}

      <div className={shellClassName}>
        {!embedded && (
          <div className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Share Session
              </h2>
              <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {sessionTitle}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className={`flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('existing')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'existing'
                ? tabActiveClass
                : isDarkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Existing Links ({shareLinks.length})
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'create'
                ? tabActiveClass
                : isDarkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Create New Link
          </button>
          <button
            onClick={() => setActiveTab('access')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'access'
                ? tabActiveClass
                : isDarkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Session Access
          </button>
        </div>

        {/* Content */}
        <div className={contentClassName}>
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {activeTab === 'access' ? (
            <SessionAccessManager
              sessionId={sessionId}
              sessionTitle={sessionTitle}
              isActive={isOpen && activeTab === 'access'}
            />
          ) : isLoading && activeTab === 'existing' ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-yellow" />
            </div>
          ) : activeTab === 'existing' ? (
            <div className="p-6">
              {shareLinks.length === 0 ? (
                <div className="text-center py-12">
                  <Link className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    No share links yet
                  </p>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Create your first share link to allow public access to this session
                  </p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className={primaryButtonClass}
                  >
                    Create Share Link
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {shareLinks.map(link => (
                    <div
                      key={link.id}
                      className={`p-4 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {link.label}
                          </h3>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span className={`flex items-center gap-1 ${
                              link.status === 'active' ? 'text-brand-yellow' :
                              link.status === 'expired' ? 'text-red-500' :
                              'text-gray-500'
                            }`}>
                              {link.status === 'active' ? <Check className="w-3 h-3" /> :
                               link.status === 'expired' ? <X className="w-3 h-3" /> :
                               <EyeOff className="w-3 h-3" />}
                              {link.status}
                            </span>
                            <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              <Clock className="w-3 h-3" />
                              Expires: {formatExpiry(link.expiresAt)}
                            </span>
                            <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              <Users className="w-3 h-3" />
                              {link.maxUniqueIps 
                                ? `${link.uniqueIps || link.uniqueViews || 0}/${link.maxUniqueIps} unique IPs` 
                                : `${link.uniqueIps || link.uniqueViews || 0} unique IPs`}
                            </span>
                            {link.totalViews > 0 && (
                              <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <Eye className="w-3 h-3" />
                                {link.maxTotalViews
                                  ? `${link.totalViews}/${link.maxTotalViews} total views`
                                  : `${link.totalViews} total views`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => fetchLogs(link)}
                            className={`p-2 rounded transition-colors ${
                              isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                            }`}
                            title="View activity logs"
                          >
                            <Activity className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => editShareLink(link)}
                            className={`p-2 rounded transition-colors ${
                              isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                            }`}
                            title="Edit link"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleShareLink(link.id, link.isEnabled)}
                            className={`p-2 rounded transition-colors ${
                              isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                            }`}
                            title={link.isEnabled ? 'Disable link' : 'Enable link'}
                          >
                            {link.isEnabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => deleteShareLink(link.id)}
                            className={`p-2 rounded transition-colors ${
                              isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                            }`}
                            title="Delete link"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className={`flex items-center gap-2 p-2 rounded border ${
                        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                      }`}>
                        <input
                          type="text"
                          value={link.url}
                          readOnly
                          className={`flex-1 bg-transparent text-sm ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        />
                        <button
                          onClick={() => handleCopyToClipboard(link.url, link.id)}
                          className={`p-2 rounded transition-colors ${
                            copiedLinkId === link.id
                              ? 'bg-brand-yellow text-brand-navy'
                              : isDarkMode
                                ? 'hover:bg-gray-700'
                                : 'hover:bg-gray-100'
                          }`}
                        >
                          {copiedLinkId === link.id ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      
                      {/* Permissions */}
                      <div className="mt-3">
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2 font-medium`}>
                          Enabled Features:
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                            isDarkMode ? 'bg-brand-yellow/15 text-brand-yellow' : 'bg-[#fff3d6] text-[#7a560c]'
                          }`}>
                            <FileText className="w-3 h-3" /> Transcript (Always On)
                          </span>
                          {/* Timeline is always available via interactive timeline at bottom */}
                          {link.chatMode !== 'DISABLED' && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-brand-navy text-brand-cloud' : 'bg-[#e8edf2] text-brand-navy'
                            }`}>
                              <MessageSquare className="w-3 h-3" /> Chat (BYOK)
                            </span>
                          )}
                          {link.commentsRead && !link.commentsWrite && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-[#4c4022] text-[#ffd37a]' : 'bg-[#fff3d6] text-[#7a560c]'
                            }`}>
                              <MessageSquare className="w-3 h-3" /> Comments (Read Only)
                            </span>
                          )}
                          {link.commentsWrite && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-brand-navy text-brand-cloud' : 'bg-[#e8edf2] text-brand-navy'
                            }`}>
                              <MessageSquare className="w-3 h-3" /> Comments (Read/Write)
                            </span>
                          )}
                          {link.descriptionEnabled && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-[#384456] text-[#d8dee6]' : 'bg-[#ebe7de] text-brand-navy'
                            }`}>
                              <FileEdit className="w-3 h-3" /> Description
                            </span>
                          )}
                          {link.rankingEnabled && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-[#233546] text-[#D3E4F5]' : 'bg-[#E7F1FB] text-[#35506C]'
                            }`}>
                              <TrendingUp className="w-3 h-3" /> Topic Map
                            </span>
                          )}
                          {link.questionsEnabled && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-[#25354C] text-[#D8E3F2]' : 'bg-[#EAF1F8] text-[#36506D]'
                            }`}>
                              <span className="flex h-3 w-3 items-center justify-center rounded-full border border-current text-[8px] font-bold leading-none">Q</span>
                              Questions
                            </span>
                          )}
                        </div>

                        {/* Restrictions */}
                        {(link.maxUniqueIps || link.maxTotalViews || link.expiresAt) && (
                          <>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2 font-medium mt-3`}>
                              Restrictions:
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {link.maxUniqueIps && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                                  (link.uniqueIps || 0) >= link.maxUniqueIps
                                    ? isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'
                                    : isDarkMode ? 'bg-brand-yellow/15 text-brand-yellow' : 'bg-[#fff3d6] text-[#7a560c]'
                                }`}>
                                  <Users className="w-3 h-3" />
                                  {link.uniqueIps || 0}/{link.maxUniqueIps} Unique IPs
                                </span>
                              )}
                              {link.maxTotalViews && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                                  (link.totalViews || 0) >= link.maxTotalViews
                                    ? isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'
                                    : isDarkMode ? 'bg-brand-yellow/15 text-brand-yellow' : 'bg-[#fff3d6] text-[#7a560c]'
                                }`}>
                                  <Eye className="w-3 h-3" />
                                  {link.totalViews || 0}/{link.maxTotalViews} Total Views
                                </span>
                              )}
                              {link.expiresAt && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                                  new Date(link.expiresAt) < new Date()
                                    ? isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'
                                    : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  <Clock className="w-3 h-3" />
                                  {new Date(link.expiresAt) < new Date() ? 'Expired' : `Expires: ${formatExpiry(link.expiresAt)}`}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      
                      {/* Activity Chart */}
                      <div className="mt-4">
                        <button
                          onClick={() => {
                            if (activityData[link.id] && expandedActivityGraphs[link.id]) {
                              // Collapse
                              setExpandedActivityGraphs(prev => ({ ...prev, [link.id]: false }));
                            } else if (activityData[link.id]) {
                              // Expand existing data
                              setExpandedActivityGraphs(prev => ({ ...prev, [link.id]: true }));
                            } else {
                              // Fetch and expand
                              fetchActivityData(link.id);
                              setExpandedActivityGraphs(prev => ({ ...prev, [link.id]: true }));
                            }
                          }}
                          className={`text-sm flex items-center gap-2 ${
                            isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'
                          }`}
                        >
                          <TrendingUp className="w-4 h-4" />
                          {loadingActivity[link.id] ? 'Loading activity...' : 
                           activityData[link.id] && expandedActivityGraphs[link.id] ? 'Hide activity graph' : 'Show activity graph'}
                          {activityData[link.id] && !loadingActivity[link.id] && (
                            expandedActivityGraphs[link.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        
                        {/* Refresh button when expanded */}
                        {activityData[link.id] && expandedActivityGraphs[link.id] && (
                          <button
                            onClick={() => fetchActivityData(link.id, activityTimeRange[link.id])}
                            className={`ml-4 text-xs ${
                              isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-500 hover:text-gray-600'
                            }`}
                          >
                            Refresh
                          </button>
                        )}
                        
                        {activityData[link.id] && expandedActivityGraphs[link.id] && (
                          <div className={`mt-3 p-3 rounded-lg ${
                            isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                          }`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-medium ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Activity</span>
                                <div className="flex gap-1">
                                  {['24hours', '7days', '30days', 'all'].map((range) => (
                                    <button
                                      key={range}
                                      onClick={() => fetchActivityData(link.id, range)}
                                      className={`px-2 py-1 text-xs rounded transition-colors ${
                                        activityTimeRange[link.id] === range
                                          ? isDarkMode
                                            ? 'bg-gray-700 text-white'
                                            : 'bg-gray-300 text-gray-900'
                                          : isDarkMode
                                            ? 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                      }`}
                                    >
                                      {range === '24hours' ? '24h' : 
                                       range === '7days' ? '7d' : 
                                       range === '30days' ? '30d' : 'All'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-xs">
                                <span className={`flex items-center gap-1 ${
                                  isDarkMode ? 'text-[#d8dee6]' : 'text-brand-navy'
                                }`}>
                                  <div className="w-2 h-2 bg-brand-navy rounded-full" />
                                  Views
                                </span>
                                <span className={`flex items-center gap-1 ${
                                  isDarkMode ? 'text-brand-yellow' : 'text-[#9c7010]'
                                }`}>
                                  <div className="w-2 h-2 bg-brand-yellow rounded-full" />
                                  Unique IPs
                                </span>
                              </div>
                            </div>
                            
                            {activityData[link.id].length === 0 ? (
                              <div className={`text-center py-8 text-sm ${
                                isDarkMode ? 'text-gray-500' : 'text-gray-500'
                              }`}>
                                No activity in this time period
                              </div>
                            ) : (
                              <>
                                <div className="h-32 flex items-end gap-1">
                                  {(() => {
                                    const data = activityData[link.id];
                                    const maxValue = Math.max(...data.map((d: any) => Math.max(d.views, d.uniqueIps)), 1);
                                    
                                    // Debug: log scaling details
                                    console.log('Chart scaling:', {
                                      dataLength: data.length,
                                      maxValue,
                                      viewsRange: [Math.min(...data.map((d: any) => d.views)), Math.max(...data.map((d: any) => d.views))],
                                      uniqueIpsRange: [Math.min(...data.map((d: any) => d.uniqueIps)), Math.max(...data.map((d: any) => d.uniqueIps))],
                                      sampleItems: data.slice(0, 3).map((d: any) => ({ views: d.views, uniqueIps: d.uniqueIps }))
                                    });
                                    
                                    return data.map((item: any, index: number) => {
                                      const formatDate = (date: Date, bucketSize: string) => {
                                        if (bucketSize === 'hour') {
                                          return date.toLocaleString('en-US', { 
                                            month: 'short', 
                                            day: 'numeric', 
                                            hour: '2-digit' 
                                          });
                                        } else if (bucketSize === 'day') {
                                          return date.toLocaleDateString('en-US', { 
                                            month: 'short', 
                                            day: 'numeric' 
                                          });
                                        } else {
                                          return date.toLocaleDateString('en-US', { 
                                            month: 'short', 
                                            day: 'numeric' 
                                          });
                                        }
                                      };
                                      
                                      return (
                                        <div
                                          key={index}
                                          className="flex-1 flex items-end justify-center gap-1 min-w-[4px] max-w-[40px] h-full"
                                          title={`${formatDate(item.time, item.bucketSize)}: ${item.views} views, ${item.uniqueIps} unique IPs`}
                                        >
                                          <div
                                            className="w-1/2 bg-brand-navy rounded-t transition-all duration-300"
                                            style={{
                                              height: `${(item.views / maxValue) * 100}%`,
                                              minHeight: item.views > 0 ? '3px' : '0'
                                            }}
                                          />
                                          <div
                                            className="w-1/2 bg-brand-yellow rounded-t transition-all duration-300"
                                            style={{
                                              height: `${(item.uniqueIps / maxValue) * 100}%`,
                                              minHeight: item.uniqueIps > 0 ? '3px' : '0'
                                            }}
                                          />
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                                <div className={`mt-2 text-xs ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-600'
                                }`}>
                                  {activityData[link.id][0]?.time.toLocaleDateString()} - {activityData[link.id][activityData[link.id].length - 1]?.time.toLocaleDateString()}
                                  {' • '}
                                  Total: {activityData[link.id].reduce((sum: number, d: any) => sum + d.views, 0)} views, {
                                    [...new Set(activityData[link.id].flatMap((d: any) => 
                                      Array.from({ length: d.uniqueIps }, (_, i) => `${d.time}-${i}`)
                                    ))].length
                                  } unique IPs
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); editingLinkId ? updateShareLink() : createShareLink(); }} className="space-y-6">
                {/* Basic Settings */}
                <div>
                  <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {editingLinkId ? 'Edit Share Link' : 'Basic Settings'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Label (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.label}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        placeholder="e.g., Public review link"
                        className={inputClass}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Expiry
                      </label>
                      <select
                        value={formData.expiresIn}
                        onChange={(e) => setFormData({ ...formData, expiresIn: e.target.value })}
                        className={inputClass}
                      >
                        <option value="never">Never (Recommended)</option>
                        <option value="8760">1 year</option>
                        <option value="720">1 month</option>
                        <option value="168">1 week</option>
                        <option value="24">24 hours</option>
                        <option value="12">12 hours</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Max Unique IP Addresses (optional)
                      </label>
                      <input
                        type="number"
                        value={formData.maxUniqueIps}
                        onChange={(e) => setFormData({ ...formData, maxUniqueIps: e.target.value })}
                        placeholder="Unlimited"
                        min="1"
                        className={inputClass}
                      />
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Limits the number of unique IP addresses that can access this link
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Max Total Views (optional)
                      </label>
                      <input
                        type="number"
                        value={formData.maxTotalViews}
                        onChange={(e) => setFormData({ ...formData, maxTotalViews: e.target.value })}
                        placeholder="Unlimited"
                        min="1"
                        className={inputClass}
                      />
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Limits the total number of times this link can be accessed (including repeat visits)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tab Permissions */}
                <div>
                  <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Tab Permissions
                  </h3>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        className={checkboxClass}
                      />
                      <Globe className="w-4 h-4" />
                      <span>Transcripts (always enabled)</span>
                    </label>

                    {/* Timeline is now always available via the interactive timeline at the bottom */}

                    <div className={`rounded-lg border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                      <label className={`flex items-start gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <input
                          type="checkbox"
                          checked={formData.chatEnabled}
                          onChange={(e) => setFormData({ ...formData, chatEnabled: e.target.checked })}
                          className={checkboxClass}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            <span>Chat with Transcript</span>
                          </div>
                          <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Public viewers must bring their own OpenRouter API key to use chat.
                          </p>
                        </div>
                      </label>
                    </div>

                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData.rankingEnabled}
                        onChange={(e) => setFormData({ ...formData, rankingEnabled: e.target.checked })}
                        className={checkboxClass}
                      />
                      <TrendingUp className="w-4 h-4" />
                      <span>Topic Map</span>
                    </label>

                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData.descriptionEnabled}
                        onChange={(e) => setFormData({ ...formData, descriptionEnabled: e.target.checked })}
                        className={checkboxClass}
                      />
                      <FileEdit className="w-4 h-4" />
                      <span>Description (YouTube Description Editor)</span>
                    </label>

                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData.contextFilesEnabled}
                        onChange={(e) => setFormData({ ...formData, contextFilesEnabled: e.target.checked })}
                        className={checkboxClass}
                      />
                      <Paperclip className="w-4 h-4" />
                      <span>Context Files (Reference Documents)</span>
                    </label>

                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData.questionsEnabled}
                        onChange={(e) => setFormData({ ...formData, questionsEnabled: e.target.checked })}
                        className={checkboxClass}
                      />
                      <span className="flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] font-bold leading-none">Q</span>
                      <span>Questions Tab</span>
                    </label>
                  </div>
                </div>

                {/* Comments Settings */}
                <div>
                  <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Comments Settings
                  </h3>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData.commentsRead}
                        onChange={(e) => setFormData({ ...formData, commentsRead: e.target.checked })}
                        className={checkboxClass}
                      />
                      <span>Allow reading comments</span>
                    </label>
                    
                    <label className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData.commentsWrite}
                        onChange={(e) => setFormData({ ...formData, commentsWrite: e.target.checked })}
                        className={checkboxClass}
                      />
                      <span>Allow posting comments (anonymous)</span>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-4">
                  {editingLinkId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingLinkId(null);
                        setActiveTab('existing');
                        setFormData({
                          label: '',
                          expiresIn: 'never',
                          maxUniqueIps: '',
                          maxTotalViews: '',
                          chatEnabled: false,
                          rankingEnabled: true,
                          rankingTtmlAllowed: false,
                          commentsRead: false,
                          commentsWrite: false,
                          descriptionEnabled: false,
                          contextFilesEnabled: true,
                          questionsEnabled: false,
                        });
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      Cancel Edit
                    </button>
                  )}
                  {!embedded && (
                    <button
                      type="button"
                      onClick={onClose}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {editingLinkId ? 'Close' : 'Cancel'}
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={primaryButtonClass}
                  >
                    {isLoading ? (editingLinkId ? 'Updating...' : 'Creating...') : (editingLinkId ? 'Update Share Link' : 'Create Share Link')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      
      {/* Logs Modal */}
      {showLogsModal && selectedLinkForLogs && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowLogsModal(false)} />
          
          <div className={`relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Logs Header */}
            <div className={`flex items-center justify-between p-6 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Activity Logs
                </h2>
                <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {selectedLinkForLogs.label || 'Share Link'}
                </p>
              </div>
              <button
                onClick={() => setShowLogsModal(false)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Logs Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Grouping Controls */}
              <div className="mb-4 flex items-center gap-4">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Group by:
                </span>
                <div className="flex gap-2">
                  {[
                    { value: 'none', label: 'None' },
                    { value: 'ip', label: 'IP Address' },
                    { value: 'action', label: 'Action' },
                    { value: 'user-agent', label: 'User Agent' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setLogsGroupBy(option.value as any)}
                      className={`px-3 py-1 text-xs rounded transition-colors ${
                        logsGroupBy === option.value
                          ? isDarkMode
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-300 text-gray-900'
                          : isDarkMode
                            ? 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-yellow" />
                </div>
              ) : accessLogs.length === 0 ? (
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No access logs yet
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    // Group logs if needed
                    if (logsGroupBy === 'none') {
                      return accessLogs.map((log, index) => (
                        <div
                          key={log.id || index}
                          className={`p-4 rounded-lg border ${
                            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`text-sm font-medium ${
                                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                  {log.event}
                                </span>
                                <span className={`text-xs ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-500'
                                }`}>
                                  {new Date(log.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <div className={`text-sm space-y-1 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                <div>IP: {log.ip_address}</div>
                                <div className="text-xs truncate">User Agent: {log.user_agent}</div>
                                {log.metadata && Object.keys(log.metadata).length > 0 && (
                                  <div className="text-xs">
                                    Metadata: {JSON.stringify(log.metadata)}
                                  </div>
                                )}
                              </div>
                            </div>
                            {log.cost_usd > 0 && (
                              <span className="text-sm text-red-500">
                                ${log.cost_usd.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      ));
                    } else {
                      // Group logs
                      const grouped: Record<string, any[]> = {};
                      accessLogs.forEach(log => {
                        const key = logsGroupBy === 'ip' ? log.ip_address :
                                   logsGroupBy === 'action' ? log.event :
                                   logsGroupBy === 'user-agent' ? log.user_agent : '';
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(log);
                      });

                      return Object.entries(grouped).map(([groupKey, logs]) => (
                        <div
                          key={groupKey}
                          className={`rounded-lg border ${
                            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className={`p-3 border-b ${
                            isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-100 border-gray-300'
                          }`}>
                            <div className="flex items-center justify-between">
                              <h4 className={`font-medium ${
                                isDarkMode ? 'text-gray-200' : 'text-gray-800'
                              }`}>
                                {groupKey}
                              </h4>
                              <span className={`text-sm ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {logs.length} events
                              </span>
                            </div>
                          </div>
                          <div className="p-3 space-y-2">
                            {/* Summary stats */}
                            <div className={`grid grid-cols-3 gap-4 mb-3 text-xs ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              <div>
                                <span className="font-medium">First:</span> {new Date(Math.min(...logs.map(l => new Date(l.timestamp).getTime()))).toLocaleString()}
                              </div>
                              <div>
                                <span className="font-medium">Last:</span> {new Date(Math.max(...logs.map(l => new Date(l.timestamp).getTime()))).toLocaleString()}
                              </div>
                              {logs.some(l => l.cost_usd > 0) && (
                                <div>
                                  <span className="font-medium">Total Cost:</span> ${logs.reduce((sum, l) => sum + (l.cost_usd || 0), 0).toFixed(2)}
                                </div>
                              )}
                            </div>
                            {/* Event breakdown */}
                            <div className={`text-xs ${
                              isDarkMode ? 'text-gray-500' : 'text-gray-600'
                            }`}>
                              {(() => {
                                const eventCounts: Record<string, number> = {};
                                logs.forEach(l => {
                                  eventCounts[l.event] = (eventCounts[l.event] || 0) + 1;
                                });
                                return Object.entries(eventCounts).map(([event, count]) => (
                                  <span key={event} className="inline-block mr-3">
                                    {event}: {count}
                                  </span>
                                ));
                              })()}
                            </div>
                          </div>
                        </div>
                      ));
                    }
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareModal;
